import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { STEPS, STEP_DEFINITIONS, TRANSITION_STEPS, MAX_STEP } from './onboardingSteps'
import { useToken } from './hooks/useToken'
import { saveProgress, completeProgress } from './api/onboardingProgressAPI'
import { readWizardData } from './hooks/useWizardData'
import { useWizardAutoSave } from './hooks/useWizardAutoSave'
import { serverURL } from './api/config'
import OnboardingUploadWatcher from './components/OnboardingUploadWatcher'

// Forward-skip rules: when goNext lands on a generating step whose work is
// already done, jump past it so the component never mounts (no spinner flash).
const FORWARD_SKIP_RULES: ReadonlyArray<{ step: number; target: number }> = [
  { step: STEPS.PROFILE_GENERATING, target: STEPS.PROFILE_REVIEW },
  { step: STEPS.SCAN_RUNNING, target: STEPS.SCAN_MILESTONE },
  { step: STEPS.DRAFT_GENERATING, target: STEPS.DRAFT_COMPLETE },
]

// ── Context type ────────────────────────────────────────────

export interface OnboardingV2ContextType {
  // Navigation
  currentStep: number
  maxStepReached: number
  bannerSegment: string
  totalSteps: number
  goNext: () => Promise<void>
  goBack: () => Promise<void>
  goToStep: (step: number) => Promise<void>
  advanceStep: () => Promise<void>
  retreatStep: () => Promise<void>
  canContinue: boolean
  setCanContinue: (v: boolean) => void
  setBeforeNext: (fn: (() => Promise<boolean | void>) | null) => void
  setBeforeBack: (fn: (() => Promise<boolean | void>) | null) => void

  // Entity IDs
  orgUuid: string | null
  fundingNeedId: number | null
  scanJobId: string | null
  applicationId: string | null
  scanResultId: string | null
  setOrgUuid: (id: string) => void
  setFundingNeedId: (id: number | null) => void
  setScanJobId: (id: string | null) => void
  setApplicationId: (id: string | null) => void
  setScanResultId: (id: string | null) => void

  // Quality flags (hydrated from progress, used by guards)
  fundingQualityPassed: boolean
  setFundingQualityPassed: (v: boolean) => void

  // Dirty flag for generation re-runs
  staleFrom: number | null
  setStaleFrom: (v: number | null | ((prev: number | null) => number | null)) => void
  profileGenerated: boolean
  setProfileGenerated: (v: boolean) => void

  // Lifecycle
  saveStatus: 'idle' | 'saving' | 'saved' | 'error'
  exitOnboarding: () => Promise<void>

  // Shell nav visibility (progress steps hide nav during spinner)
  hideNav: boolean
  setHideNav: (v: boolean) => void

  // Shell nav customisation (steps can override button text / hide Back / hide Continue)
  continueLabel: string | null
  setContinueLabel: (label: string | null) => void
  hideBackButton: boolean
  setHideBackButton: (v: boolean) => void
  hideContinueButton: boolean
  setHideContinueButton: (v: boolean) => void

}

const OnboardingV2Context = createContext<OnboardingV2ContextType | null>(null)

// ── Hook ────────────────────────────────────────────────────

export function useOnboardingV2(): OnboardingV2ContextType {
  const ctx = useContext(OnboardingV2Context)
  if (!ctx) {
    throw new Error('useOnboardingV2 must be used within an OnboardingV2Provider')
  }
  return ctx
}

// ── Provider ────────────────────────────────────────────────

interface OnboardingV2ProviderProps {
  children: ReactNode
  /** Initial state for hydration from progress record */
  initialState?: {
    currentStep?: number
    maxStepReached?: number
    orgUuid?: string | null
    fundingNeedId?: number | null
    scanJobId?: string | null
    applicationId?: string | null
    scanResultId?: string | null
    fundingQualityPassed?: boolean
    staleFrom?: number | null
    profileGenerated?: boolean
  }
}

export function OnboardingV2Provider({ children, initialState }: OnboardingV2ProviderProps) {
  const navigate = useNavigate()
  const { getToken } = useToken()
  const queryClient = useQueryClient()

  // Navigation state
  const [currentStep, setCurrentStep] = useState(initialState?.currentStep ?? 1)
  const [maxStepReached, setMaxStepReached] = useState(initialState?.maxStepReached ?? 1)
  const [canContinue, setCanContinue] = useState(true)
  const [hideNav, setHideNav] = useState(false)
  const [continueLabel, setContinueLabel] = useState<string | null>(null)
  const [hideBackButton, setHideBackButton] = useState(false)
  const [hideContinueButton, setHideContinueButton] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')

  // Quality flags (writable — persisted through persistProgress and pagehide flush)
  const [fundingQualityPassed, setFundingQualityPassed] = useState(initialState?.fundingQualityPassed ?? false)
  const fundingQualityPassedRef = useRef(fundingQualityPassed)
  fundingQualityPassedRef.current = fundingQualityPassed

  // Dirty flag for generation re-runs
  const [staleFrom, setStaleFrom] = useState<number | null>(initialState?.staleFrom ?? null)
  const [profileGenerated, setProfileGenerated] = useState(initialState?.profileGenerated ?? false)
  const staleFromRef = useRef(staleFrom)
  staleFromRef.current = staleFrom
  const profileGeneratedRef = useRef(profileGenerated)
  profileGeneratedRef.current = profileGenerated

  // Entity IDs — refs are updated eagerly by the wrapped setters so
  // persistProgress (called immediately after beforeNext) always reads fresh values.
  const [orgUuid, _setOrgUuid] = useState<string | null>(initialState?.orgUuid ?? null)
  const [fundingNeedId, _setFundingNeedId] = useState<number | null>(initialState?.fundingNeedId ?? null)
  const [scanJobId, _setScanJobId] = useState<string | null>(initialState?.scanJobId ?? null)
  const [applicationId, _setApplicationId] = useState<string | null>(initialState?.applicationId ?? null)
  const [scanResultId, _setScanResultId] = useState<string | null>(initialState?.scanResultId ?? null)

  const orgUuidRef = useRef(orgUuid)
  const fundingNeedIdRef = useRef(fundingNeedId)
  const scanJobIdRef = useRef(scanJobId)
  const applicationIdRef = useRef(applicationId)
  const scanResultIdRef = useRef(scanResultId)

  // Wrapped setters: update ref immediately so persistProgress reads fresh values
  // even before React re-renders (fixes race between beforeNext → goNext → persist)
  const setOrgUuid = useCallback((v: string | null) => { orgUuidRef.current = v; _setOrgUuid(v) }, [])
  const setFundingNeedId = useCallback((v: number | null) => { fundingNeedIdRef.current = v; _setFundingNeedId(v) }, [])
  const setScanJobId = useCallback((v: string | null) => { scanJobIdRef.current = v; _setScanJobId(v) }, [])
  const setApplicationId = useCallback((v: string | null) => { applicationIdRef.current = v; _setApplicationId(v) }, [])
  const setScanResultId = useCallback((v: string | null) => { scanResultIdRef.current = v; _setScanResultId(v) }, [])

  // Step refs for pagehide flush
  const currentStepRef = useRef(currentStep)
  currentStepRef.current = currentStep
  const maxStepReachedRef = useRef(maxStepReached)
  maxStepReachedRef.current = maxStepReached
  const lastTokenRef = useRef<string>('')

  const beforeNextRef = useRef<(() => Promise<boolean | void>) | null>(null)
  const beforeBackRef = useRef<(() => Promise<boolean | void>) | null>(null)
  const totalSteps = STEP_DEFINITIONS.length

  const bannerSegment = useMemo(
    () => STEP_DEFINITIONS.find(d => d.id === currentStep)?.segment ?? '',
    [currentStep]
  )

  // Internal save helper — reads wizard_data from localStorage (always fresh)
  const persistProgress = useCallback(async (step: number, maxStep: number) => {
    const currentOrgUuid = orgUuidRef.current
    if (!currentOrgUuid) {
      console.warn('[onboarding] persistProgress skipped — orgUuid is null')
      return
    }
    setSaveStatus('saving')
    try {
      const token = await getToken()
      lastTokenRef.current = token
      await saveProgress(token, currentOrgUuid, {
        current_step: step,
        max_step_reached: maxStep,
        funding_need_id: fundingNeedIdRef.current,
        scan_job_id: scanJobIdRef.current,
        application_id: applicationIdRef.current,
        scan_result_id: scanResultIdRef.current,
        funding_quality_passed: fundingQualityPassedRef.current,
        stale_from: staleFromRef.current,
        profile_generated: profileGeneratedRef.current,
        wizard_data: readWizardData(),
      })
      setSaveStatus('saved')
    } catch (err) {
      console.error('[onboarding] persistProgress failed:', err)
      setSaveStatus('error')
    }
  }, [getToken])

  // Flush progress on tab close / navigation away (best-effort keepalive)
  useEffect(() => {
    const flush = () => {
      const org = orgUuidRef.current
      const token = lastTokenRef.current
      if (!org || !token) return

      const payload = {
        current_step: currentStepRef.current,
        max_step_reached: maxStepReachedRef.current,
        funding_need_id: fundingNeedIdRef.current,
        scan_job_id: scanJobIdRef.current,
        application_id: applicationIdRef.current,
        scan_result_id: scanResultIdRef.current,
        funding_quality_passed: fundingQualityPassedRef.current,
        stale_from: staleFromRef.current,
        profile_generated: profileGeneratedRef.current,
        wizard_data: readWizardData(),
      }

      fetch(`${serverURL}/onboarding-progress/${org}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        keepalive: true,
      })
    }

    const popstateFlush = () => {
      persistProgress(currentStepRef.current, maxStepReachedRef.current)
    }

    window.addEventListener('pagehide', flush)
    window.addEventListener('popstate', popstateFlush)
    return () => {
      window.removeEventListener('pagehide', flush)
      window.removeEventListener('popstate', popstateFlush)
      // Flush on unmount (covers SPA navigation away via nav links)
      flush()
    }
  }, [persistProgress])

  // Auto-save to DB after 30s of wizard data inactivity
  useWizardAutoSave(persistProgress, currentStepRef, maxStepReachedRef)

  // Navigation callbacks — update refs eagerly (before React re-renders) so
  // pagehide/unmount flush always reads the latest values, matching the pattern
  // used for entity IDs (lines 153-157).
  const goNext = useCallback(async () => {
    let nextStep = Math.min(currentStepRef.current + 1, MAX_STEP)

    // Forward-skip: if the target is a generating step whose work is already
    // done, jump past it so the component never mounts (no spinner flash).
    const rule = FORWARD_SKIP_RULES.find(r => r.step === nextStep)
    if (rule) {
      const sf = staleFromRef.current
      const notStale = !sf || sf > rule.step
      let shouldSkip = false
      if (nextStep === STEPS.PROFILE_GENERATING) {
        shouldSkip = profileGeneratedRef.current && notStale
      } else if (nextStep === STEPS.SCAN_RUNNING) {
        shouldSkip = !!readWizardData()?.scanCompleted && notStale
      } else if (nextStep === STEPS.DRAFT_GENERATING) {
        shouldSkip = !!applicationIdRef.current && notStale
      }
      if (shouldSkip) nextStep = rule.target
    }

    const newMax = Math.max(maxStepReachedRef.current, nextStep)
    currentStepRef.current = nextStep
    maxStepReachedRef.current = newMax
    setCurrentStep(nextStep)
    setMaxStepReached(newMax)
    await persistProgress(nextStep, newMax)
  }, [persistProgress])

  const goBack = useCallback(async () => {
    let target = currentStepRef.current - 1
    // Skip transition steps
    while (target > 1 && TRANSITION_STEPS.has(target)) {
      target--
    }
    // Skip guided step for write-path users (they never visit step 14).
    if (target === STEPS.FUNDING_GUIDED) {
      const data = readWizardData()
      if (data?.fundingMode === 'write') {
        target = STEPS.FUNDING_CREATE
      }
    }
    currentStepRef.current = target
    setCurrentStep(target)
    await persistProgress(target, maxStepReachedRef.current)
  }, [persistProgress])

  const goToStep = useCallback(async (step: number) => {
    const newMax = Math.max(maxStepReachedRef.current, step)
    currentStepRef.current = step
    maxStepReachedRef.current = newMax
    setCurrentStep(step)
    setMaxStepReached(newMax)
    await persistProgress(step, newMax)
  }, [persistProgress])

  const advanceStep = useCallback(async () => {
    if (beforeNextRef.current) {
      const result = await beforeNextRef.current()
      if (result === false) return
    }
    await goNext()
  }, [goNext])

  const setBeforeNext = useCallback((fn: (() => Promise<boolean | void>) | null) => {
    beforeNextRef.current = fn
  }, [])

  const retreatStep = useCallback(async () => {
    if (beforeBackRef.current) {
      const result = await beforeBackRef.current()
      if (result === false) return
    }
    await goBack()
  }, [goBack])

  const setBeforeBack = useCallback((fn: (() => Promise<boolean | void>) | null) => {
    beforeBackRef.current = fn
  }, [])

  const exitOnboarding = useCallback(async () => {
    const step = currentStepRef.current
    const maxStep = maxStepReachedRef.current
    const orgUuid = orgUuidRef.current

    // persistProgress before completeProgress: /complete's server-side
    // guard reads the PERSISTED max_step_reached, not what the client
    // thinks it is. Firing completeProgress before this persist lands can
    // 400 even though the client legitimately reached the step. Still
    // fire-and-forget relative to navigate() below — no need to block
    // navigation on either round-trip, only to order them against each
    // other.
    const completion = persistProgress(step, maxStep).then(() => {
      // Reaching match results and then explicitly leaving counts as finished,
      // same as drafting an application does (DraftComplete.tsx) - not
      // reaching the step alone, only actually exiting.
      if (maxStep >= STEPS.SCAN_RESULTS && orgUuid) {
        return getToken()
          .then((token) => completeProgress(token, orgUuid))
          .then(() => {
            // Sync the cache MainLayout's GuardedOutlet gates on — without this,
            // its 5-minute staleTime keeps treating the org as mid-onboarding
            // and blocks match/application/funding routes right after exiting.
            queryClient.setQueryData(
              ['onboarding-status', orgUuid],
              (old: Record<string, unknown> | null | undefined) =>
                old ? { ...old, is_complete: true } : { is_complete: true }
            )
            queryClient.invalidateQueries({ queryKey: ['onboarding-status'] })
          })
      }
    })
    completion.catch((err) => {
      // Deliberately no user-visible message: matches persistProgress's own
      // precedent (best-effort, silent on failure), and the user has
      // already navigated away by the time this settles. If completion
      // truly failed, MainLayout's GuardedOutlet remains the safety net —
      // the user hits the existing "Complete Your Onboarding" gate rather
      // than nothing at all. What changes here is that this is no longer
      // invisible to us: console.error only becomes a Sentry breadcrumb,
      // never an error event, so report it properly with enough context to
      // investigate.
      Sentry.captureException(err, {
        extra: { orgUuid, maxStepReached: maxStep },
        tags: { feature: 'onboarding-completion' },
      })
    })

    localStorage.setItem('onboarding_exited', '1')
    navigate('/profile/details')
    // Note: no ['currentUser'] invalidation here — the useAddOrg mutation
    // already invalidated it at step 4-5.
  }, [persistProgress, navigate, getToken, queryClient])

  const value = useMemo<OnboardingV2ContextType>(
    () => ({
      currentStep,
      maxStepReached,
      bannerSegment,
      totalSteps,
      goNext,
      goBack,
      goToStep,
      advanceStep,
      retreatStep,
      canContinue,
      setCanContinue,
      setBeforeNext,
      setBeforeBack,
      orgUuid,
      fundingNeedId,
      scanJobId,
      applicationId,
      scanResultId,
      setOrgUuid,
      setFundingNeedId,
      setScanJobId,
      setApplicationId,
      setScanResultId,
      fundingQualityPassed,
      setFundingQualityPassed,
      staleFrom,
      setStaleFrom,
      profileGenerated,
      setProfileGenerated,
      hideNav,
      setHideNav,
      continueLabel,
      setContinueLabel,
      hideBackButton,
      setHideBackButton,
      hideContinueButton,
      setHideContinueButton,
      saveStatus,
      exitOnboarding,
    }),
    [
      currentStep, maxStepReached, bannerSegment, totalSteps,
      goNext, goBack, goToStep, advanceStep, retreatStep, canContinue,
      setBeforeNext, setBeforeBack, orgUuid, fundingNeedId, scanJobId,
      applicationId, scanResultId,
      fundingQualityPassed, staleFrom, profileGenerated,
      hideNav, continueLabel, hideBackButton, hideContinueButton,
      saveStatus, exitOnboarding,
    ]
  )

  return (
    <OnboardingV2Context.Provider value={value}>
      {/* Above every step, so a website crawl started on UPLOAD_WEBPAGES keeps
          being watched once that step unmounts. */}
      <OnboardingUploadWatcher />
      {children}
    </OnboardingV2Context.Provider>
  )
}
